import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  AuthWrapper,
  FormWrapper,
  ContentLogin,
  ImageWrapper,
  Form,
  ConfirmWrapp,
  LinkWrap,
  EmptyColumn,
  ErrorMessage,
} from "../../style/Auth";

import logo from "../../images/ic_logo.svg";
import Button from "../../components/button/index";
import PlayerLoader from "../../components/PlayerLoader";

import {
  fetchSubscriptionPlans,
  createCheckoutSession,
} from "../../pages/player/api";

// /subscription/upgrade — re-subscribe path for lapsed users (PRD §3.7).
// Mirrors the Login / Register chrome for visual consistency.
const formatPrice = (amount, currency) => {
  if (typeof amount !== "number") return "";
  const value = (amount / 100).toFixed(amount % 100 === 0 ? 0 : 2);
  const symbol =
    currency?.toUpperCase() === "EUR" ? "€"
    : currency?.toUpperCase() === "USD" ? "$"
    : "";
  return `${symbol}${value}`;
};

const intervalLabel = (interval) => {
  if (interval === "month") return " /month";
  if (interval === "year") return " /year";
  return "";
};

const SubscriptionUpgrade = () => {
  const [state, setState] = useState({ loading: true, error: null, plans: [] });
  const [redirecting, setRedirecting] = useState(null); // lookup_key in flight

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchSubscriptionPlans();
        const plans = res?.data?.plans ?? [];
        if (!cancelled) setState({ loading: false, error: null, plans });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          error: err?.response?.data?.error || "Could not load plans.",
          plans: [],
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSelectPlan = async (lookupKey) => {
    if (redirecting) return;
    setRedirecting(lookupKey);
    try {
      const res = await createCheckoutSession(lookupKey);
      const url = res?.data?.url;
      if (!url) throw new Error("No checkout URL returned");
      window.location.assign(url);
    } catch (err) {
      setState((s) => ({
        ...s,
        error:
          err?.response?.data?.error ||
          err?.message ||
          "Could not start checkout.",
      }));
      setRedirecting(null);
    }
  };

  // Order: monthly first, then annual.
  const plans = [...state.plans].sort((a, b) => {
    const order = { month: 0, year: 1 };
    return (order[a.interval] ?? 9) - (order[b.interval] ?? 9);
  });

  const monthlyAmount = plans.find((p) => p.interval === "month")?.amount;
  const annualAmount = plans.find((p) => p.interval === "year")?.amount;
  const annualSavings =
    monthlyAmount && annualAmount
      ? Math.round((monthlyAmount * 12 - annualAmount) / 100)
      : null;

  return (
    <AuthWrapper>
      <FormWrapper>
        <ContentLogin>
          <EmptyColumn />
          <Form as="div">
            <a href="/">
              <img className="logo" src={logo} alt="Logo" />
            </a>
            <h1>Choose your plan</h1>
            <p>You'll be charged immediately. Cancel anytime.</p>

            {state.loading && <PlayerLoader label="loading plans" />}

            {state.error && <ErrorMessage>{state.error}</ErrorMessage>}

            {!state.loading && plans.length > 0 && (
              <Cards>
                {plans.map((plan) => {
                  const isAnnual = plan.interval === "year";
                  return (
                    <Card key={plan.lookup_key} $highlight={isAnnual}>
                      <CardName>
                        {plan.display_name || (isAnnual ? "Annual" : "Monthly")}
                      </CardName>
                      <CardPrice>
                        {formatPrice(plan.amount, plan.currency)}
                        <Interval>{intervalLabel(plan.interval)}</Interval>
                      </CardPrice>
                      {isAnnual && annualSavings != null && (
                        <Saving>Save €{annualSavings}/year vs monthly</Saving>
                      )}
                      <ConfirmWrapp>
                        <Button
                          title={
                            redirecting === plan.lookup_key
                              ? "Redirecting…"
                              : "Choose plan"
                          }
                          size="full"
                          sizeResponse="full"
                          type="button"
                          onClick={() => onSelectPlan(plan.lookup_key)}
                          disabled={!!redirecting}
                        />
                      </ConfirmWrapp>
                    </Card>
                  );
                })}
              </Cards>
            )}
          </Form>
          <LinkWrap>
            <h3>
              Need help? Email{" "}
              <a href="mailto:support@smrtstats.com">support@smrtstats.com</a>
            </h3>
          </LinkWrap>
        </ContentLogin>
      </FormWrapper>
      <ImageWrapper />
    </AuthWrapper>
  );
};

export default SubscriptionUpgrade;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 4px;
`;

const Card = styled.div`
  background: ${(p) => p.theme.cardBg ?? "#11141A"};
  border: 1px solid ${(p) => (p.$highlight ? p.theme.green : "transparent")};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CardName = styled.div`
  font-family: "SatoshiMedium", sans-serif;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => p.theme.blueish2};
`;

const CardPrice = styled.div`
  font-family: "SatoshiBold", sans-serif;
  font-size: 28px;
  color: ${(p) => p.theme.white};
`;

const Interval = styled.span`
  font-family: "SatoshiMedium", sans-serif;
  font-size: 14px;
  margin-left: 4px;
  color: ${(p) => p.theme.blueish2};
`;

const Saving = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.green};
`;
